import { fetchApi } from './api';
import type {
    CompanyCar,
    CarBrand,
    CarFuelType,
    CarLeasingCompany,
    CarInsuranceCompany,
    CarEmployee,
    CompanyOption,
    FixedAssetOption,
    CreateCarData,
    UpdateCarData,
    SpecLookupResult,
    CarDocument,
    CarDocumentMetadata,
} from '../types/companyCars';

export const companyCarsApi = {
    // Fleet CRUD
    listCars: (companyCode?: string, activeOnly?: boolean) => {
        const params = new URLSearchParams();
        if (companyCode) params.set('companyCode', companyCode);
        if (activeOnly !== undefined) params.set('activeOnly', String(activeOnly));
        const qs = params.toString();
        return fetchApi<CompanyCar[]>(`/company-cars${qs ? `?${qs}` : ''}`);
    },

    createCar: (data: CreateCarData) =>
        fetchApi<{ id: number; createdAt: string }>('/company-cars', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    updateCar: (id: number, data: UpdateCarData) =>
        fetchApi<{ id: number; updated: boolean }>(`/company-cars/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    deleteCar: (id: number) =>
        fetchApi<{ id: number; deleted: boolean }>(`/company-cars/${id}`, {
            method: 'DELETE',
        }),

    // Excel export (returns blob)
    exportCars: async (companyCode?: string, activeOnly?: boolean): Promise<Blob> => {
        const params = new URLSearchParams();
        if (companyCode) params.set('companyCode', companyCode);
        if (activeOnly !== undefined) params.set('activeOnly', String(activeOnly));
        const qs = params.toString();
        const res = await fetch(`/api/v1/company-cars/export${qs ? `?${qs}` : ''}`);
        if (!res.ok) throw new Error('Export failed');
        return res.blob();
    },

    // Employees (for driver combobox)
    getEmployees: () =>
        fetchApi<CarEmployee[]>('/company-cars/employees'),

    // Lookup: Brands
    listBrands: () =>
        fetchApi<CarBrand[]>('/company-cars/brands'),

    createBrand: (brandName: string) =>
        fetchApi<{ id: number; createdAt: string }>('/company-cars/brands', {
            method: 'POST',
            body: JSON.stringify({ brandName }),
        }),

    updateBrand: (id: number, data: { brandName?: string; sortOrder?: number; isActive?: boolean }) =>
        fetchApi<{ id: number; updated: boolean }>(`/company-cars/brands/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    // Lookup: Fuel Types
    listFuelTypes: () =>
        fetchApi<CarFuelType[]>('/company-cars/fuel-types'),

    createFuelType: (data: { fuelCode: string; fuelLabel: string; isElectric?: boolean }) =>
        fetchApi<{ id: number; createdAt: string }>('/company-cars/fuel-types', {
            method: 'POST',
            body: JSON.stringify(data),
        }),

    updateFuelType: (id: number, data: { fuelCode?: string; fuelLabel?: string; isElectric?: boolean; sortOrder?: number; isActive?: boolean }) =>
        fetchApi<{ id: number; updated: boolean }>(`/company-cars/fuel-types/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    // Lookup: Leasing Companies
    listLeasingCompanies: () =>
        fetchApi<CarLeasingCompany[]>('/company-cars/leasing-companies'),

    createLeasingCompany: (companyName: string) =>
        fetchApi<{ id: number; createdAt: string }>('/company-cars/leasing-companies', {
            method: 'POST',
            body: JSON.stringify({ companyName }),
        }),

    updateLeasingCompany: (id: number, data: { companyName?: string; sortOrder?: number; isActive?: boolean }) =>
        fetchApi<{ id: number; updated: boolean }>(`/company-cars/leasing-companies/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    // Lookup: Insurance Companies
    listInsuranceCompanies: () =>
        fetchApi<CarInsuranceCompany[]>('/company-cars/insurance-companies'),

    createInsuranceCompany: (companyName: string) =>
        fetchApi<{ id: number; createdAt: string }>('/company-cars/insurance-companies', {
            method: 'POST',
            body: JSON.stringify({ companyName }),
        }),

    updateInsuranceCompany: (id: number, data: { companyName?: string; sortOrder?: number; isActive?: boolean }) =>
        fetchApi<{ id: number; updated: boolean }>(`/company-cars/insurance-companies/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        }),

    // Companies (from etl.DolderCompany)
    getCompanies: () =>
        fetchApi<CompanyOption[]>('/company-cars/companies'),

    // Fixed Assets (from etl.FixedAsset)
    getFixedAssets: (companyCode: string) =>
        fetchApi<FixedAssetOption[]>(`/company-cars/fixed-assets?companyCode=${companyCode}`),

    // Vehicle documents (migration 224). The car id picks the vehicle, so every row of the
    // same car answers with the same list.
    listDocuments: (carId: number) =>
        fetchApi<{ plateKey: string | null; documents: CarDocument[]; sharePointCopyEnabled: boolean }>(`/company-cars/${carId}/documents`),

    /** Retry (or make) the SharePoint archive copy. A failure comes back as `spCopyError`, not as an error. */
    copyDocumentToSharePoint: (documentId: number) =>
        fetchApi<CarDocument & { alreadyCopied: boolean }>(`/company-cars/documents/${documentId}/sharepoint-copy`, {
            method: 'POST',
        }),

    uploadDocument: (carId: number, body: CarDocumentMetadata & {
        fileName: string; contentType: string; fileBase64: string;
    }) =>
        fetchApi<CarDocument & { duplicate: boolean }>(`/company-cars/${carId}/documents`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),

    updateDocument: (documentId: number, meta: CarDocumentMetadata) =>
        fetchApi<CarDocument>(`/company-cars/documents/${documentId}`, {
            method: 'PUT',
            body: JSON.stringify(meta),
        }),

    deleteDocument: (documentId: number) =>
        fetchApi<{ documentId: number; removed: boolean }>(`/company-cars/documents/${documentId}`, {
            method: 'DELETE',
        }),

    /**
     * The bytes as a Blob, for a blob: URL in the viewer. A plain `fetch`, because the body is the
     * file, not the JSON envelope; an error still arrives as the envelope and its message is shown.
     */
    fetchDocumentContent: async (documentId: number): Promise<Blob> => {
        const res = await fetch(`/api/v1/company-cars/documents/${documentId}/content`);
        const contentType = res.headers.get('content-type') || '';
        if (res.ok && !contentType.includes('application/json')) return res.blob();
        if (contentType.includes('application/json')) {
            const body = await res.json().catch(() => null);
            const message = body?.error?.message;
            if (typeof message === 'string' && message) throw new Error(message);
        }
        throw new Error(
            res.status === 502 || res.status === 504
                ? 'The document could not be fetched. Please try again shortly.'
                : `The document could not be opened (${res.status}).`
        );
    },

    // AI Spec Lookup
    lookupSpecs: (brand: string, model: string) =>
        fetchApi<SpecLookupResult>('/company-cars/lookup-specs', {
            method: 'POST',
            body: JSON.stringify({ brand, model }),
        }),
};
